/**
 * The second half of §F1's "hydrate store from hash on boot": confirming the ids a shared
 * link asked for, once the catalogues that own them have answered.
 *
 * `lib/permalink.ts` runs before the first render and therefore before `/indicators` and
 * `/layers` exist, so it can only check that an id is *shaped* like one. A link written
 * against an older deploy — or edited by hand — can name an indicator that no longer
 * exists, and the map would then sit empty with no explanation. This component compares
 * what the hash applied against what the server actually serves and cleans up:
 *
 * * **indicators** (base fill, overlays, bivariate Y) — dropped **with a toast**, because
 *   the indicator is what the reader came to see and its silent absence looks like a bug;
 * * **layers** — dropped **silently**: §I3 omits every NONE-access row for
 *   public/field/partner, so an id missing from *this* reader's `/layers` usually means
 *   "not yours to see" rather than "gone", and §I2 does not announce what is withheld.
 * * **the §F5.8 scope** (`sc=`) — *resolved*, not merely checked. `applyToStore` cannot
 *   build one: the store needs the unit's type, admin level, country and name, and none of
 *   those is inferable from an id. So the hash carries the id alone and this component asks
 *   `/hierarchy/units/{id}`. A 404 drops the filter **with a toast**, for the same reason a
 *   missing indicator gets one: the whole point of the link was the filtered view, and an
 *   unfiltered map arriving instead looks like the link worked.
 *
 * It runs at most once per boot (the pending list is cleared as soon as the matching
 * catalogue lands) and fires **no** requests at all when the hash carried no ids — the
 * common case, which must not pay for a `/layers` fetch on, say, the dashboard route.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchIndicators, fetchLayers, fetchMe, fetchUnitDetail, queryKeys } from '../lib/api';
import { clearPendingPermalinkIds, pendingPermalinkIds } from '../lib/permalink';
import { DEFAULT_INDICATOR, scopeFromUnit, useAppStore, WATERSHED_LEVEL } from '../lib/state';
import { toast } from '../lib/toast';

export function PermalinkSync(): null {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const pending = pendingPermalinkIds();
  const wantIndicators = pending.indicators.length > 0;
  const wantLayers = pending.layers.length > 0;
  /**
   * §M3.5: `lv=watershed` is only meaningful for an indicator with native watershed values,
   * and `applyToStore` cannot know — it runs before the first render. So a restored watershed
   * level is a second reason to consult `/indicators`, even for a hash carrying no ids at all
   * (`#lv=watershed`, hand-written). The default level is `1`, so this never fires on a plain
   * load, and on the map route it is a cache read: the sidebar has the same query in flight.
   */
  const wantWatershedCheck = useAppStore((s) => s.reportingLevel) === WATERSHED_LEVEL;

  const indicators = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: wantIndicators || wantWatershedCheck,
  });

  /**
   * `/layers` is role-keyed (§I1/§D5), and `role` only becomes authoritative when §D1's
   * `/auth/me` lands. Waiting for that query — the same cache entry `SessionSync` already
   * populates, so this observer costs no extra request — keeps an analyst's link from
   * having its restricted layers stripped by a `public` catalogue that was merely first.
   */
  const session = useQuery({
    queryKey: queryKeys.me(),
    queryFn: fetchMe,
    retry: false,
    staleTime: Infinity,
    enabled: wantLayers,
  });

  const layers = useQuery({
    queryKey: queryKeys.layers(role),
    queryFn: fetchLayers,
    enabled: wantLayers && session.isFetched,
  });

  /**
   * §F5.8 `sc=`. `retry: false` because the only interesting failure is the 404 that means
   * "no such unit" — the §D envelope the shared id earns when it was written against another
   * deploy — and retrying it twice would delay the toast for no gain. It is the same
   * `['unit', id]` cache entry the §F5.7 profile and the search box use, so a scope the
   * reader then clicks on costs nothing.
   */
  const wantScope = pending.scope[0];
  const scopeUnit = useQuery({
    queryKey: queryKeys.unit(wantScope ?? ''),
    queryFn: () => fetchUnitDetail(wantScope as string),
    enabled: Boolean(wantScope),
    retry: false,
  });

  const known = indicators.data;
  useEffect(() => {
    if (!known) return;
    const ids = pendingPermalinkIds().indicators;
    if (ids.length) {
      clearPendingPermalinkIds('indicators');

      // Two ways a shared hash can name something the map must not draw: the id is not in
      // the catalogue at all, or it is there but demo-only (`active: false`). Both are
      // "drop it", so they share one set — but they are worth *saying* differently, because
      // a link that stops working is a bug report and a link to a disabled indicator is not.
      const missing = ids.filter((id) => !known.some((i) => i.id === id));
      const inactive = ids.filter((id) => known.some((i) => i.id === id && !i.active));
      const gone = new Set([...missing, ...inactive]);
      if (gone.size) {
        // The two cases part company on the *base* indicator only. An id the catalogue has
        // never heard of means the link is broken or from another deployment, and guessing a
        // substitute would misrepresent what was shared — that stays `null`. A demo-only id
        // is a link to something real that is merely switched off, so the map opens on the
        // default instead of blank. Overlays and the bivariate Y are dropped either way:
        // there is no sensible stand-in for "the second thing someone chose".
        const inactiveSet = new Set(inactive);
        const fallback = known.find((i) => i.id === DEFAULT_INDICATOR)?.active
          ? DEFAULT_INDICATOR
          : null;
        useAppStore.setState((s) => {
          const indicator = !s.indicator || !gone.has(s.indicator)
            ? s.indicator
            : inactiveSet.has(s.indicator)
              ? fallback
              : null;
          return {
            indicator,
            overlays: s.overlays.filter((o) => !gone.has(o.id) && o.id !== indicator),
            // §F5.4's X is the base indicator: losing it leaves bivariate mode with nothing
            // to cross, so the mode goes with it rather than painting a blank 3×3.
            biv: !s.biv.on || indicator === null
              ? { on: false, y: null }
              : { on: true, y: s.biv.y && gone.has(s.biv.y) ? null : s.biv.y },
          };
        });
        toast(t(inactive.length ? 'tools.shareInactive' : 'tools.shareUnknown'));
      }
    }

    // §M3.5, after the drop above so it sees the indicator that actually survived: a
    // restored `lv=watershed` only stands if that indicator really has the stratum. The id is
    // passed alongside the answer so that `OperationalHierarchy`, reconciling off the same
    // catalogue landing from a render that predates the correction above, cannot demote the
    // level for an indicator this effect has just replaced (see `reconcileReportingLevel`).
    const mapped = useAppStore.getState().indicator;
    useAppStore
      .getState()
      .reconcileReportingLevel(
        mapped,
        Boolean(mapped && known.find((i) => i.id === mapped)?.has_watershed_values),
      );
  }, [known, t]);

  const { data: unit, isError: scopeGone } = scopeUnit;
  useEffect(() => {
    if (!pendingPermalinkIds().scope.length) return;
    if (!unit && !scopeGone) return; // still in flight — the map draws unfiltered meanwhile
    clearPendingPermalinkIds('scope');
    if (unit) {
      // `setScope`, not `setState`: the shared link's `c=` and `lv=` are both already
      // applied, and the action is what reconciles them with the scope (a link naming a
      // Malian watershed and `c=BFA` is a contradiction the scope wins).
      useAppStore.getState().setScope(scopeFromUnit(unit));
    } else {
      // No filter after all, so the link's `lv=` is a whole-programme level — the reading
      // `applyToStore` deliberately withheld while a scope was pending (it must not write
      // `lastAdminLevel` from a level a filter forced). Re-asserting it through the action
      // is what puts the two back in step, on the one path where the filter never lands.
      const { reportingLevel, setReportingLevel } = useAppStore.getState();
      setReportingLevel(reportingLevel);
      toast(t('hierarchy.filterGone'));
    }
  }, [unit, scopeGone, t]);

  const catalogue = layers.data;
  useEffect(() => {
    const ids = pendingPermalinkIds().layers;
    if (!ids.length || !catalogue) return;
    clearPendingPermalinkIds('layers');

    const gone = ids.filter((id) => !catalogue.some((l) => l.id === id));
    if (!gone.length) return;

    useAppStore.setState((s) => {
      const next = new Set(s.layersOn);
      for (const id of gone) next.delete(id);
      return { layersOn: next };
    });
  }, [catalogue]);

  return null;
}

export default PermalinkSync;
